/**
 * The Wiki tab: what every column means, what every value can be, and how a
 * target got its score.
 *
 * Two halves with different provenance, and the page says which is which.
 * The *prose* per column is authored in `../wiki/columns.ts` — it exists nowhere
 * else, because the harness knows how it computes `best_elo` but never explains
 * it in a sentence. The *numbers and value sets* are generated at build time from
 * the files the agents read (`scripts/build-wiki.mjs`), so a merge request that
 * changes a weight changes this page in the same commit.
 *
 * Fetches nothing. Everything here is either in the bundle or imported from the
 * module that already owns it — `STAGE_LABELS` comes from `lib/stages`, the same
 * map the run header renders, since a stage vocabulary with two homes would drift
 * the first time the harness grew a step.
 *
 * This is also the one place in the UI that expands the product name, because it
 * is the one place a first-time reader is looking for it. The header says
 * `ASTRA · Target Discovery` and nothing else repeats the expansion. No
 * identifier is renamed anywhere: the AgentCore runtime, the CLI, the hostnames
 * and the database schema keep the names they have, and any prose that quotes one
 * quotes it verbatim.
 *
 * **`SECTIONS` is the single home of this page's outline.** The sticky nav and the
 * body are both rendered from it, so a section cannot exist without a nav entry
 * pointing at it or a nav entry point at nothing — the drift a hand-maintained
 * table of contents produces on its second edit. The four surface sections derive
 * their heading, their intro and their nav label from `SURFACE_LABELS` /
 * `SURFACE_INTROS`, which stay the one home of that wording.
 */
import { useEffect, useId, useState, type ReactNode } from 'react'
import { AlertTriangle, ChevronDown } from 'lucide-react'
import wiki from '../generated/wiki.json'
import { STAGE_LABELS } from '../lib/stages'
import { COLUMN_DOCS, SURFACE_INTROS, SURFACE_LABELS } from '../wiki/columns'
import type { ColumnDoc, Surface } from '../wiki/columns'
import { Card, CardBody, CardHeader } from './ui/Card'
import { cn } from '../lib/utils'

/**
 * Reading order, which is not declaration order: a reader arrives from the run
 * tab, so the ranked targets come first and the run's own bookkeeping last.
 */
const SURFACE_ORDER: Surface[] = ['run-targets', 'board', 'dead-ends', 'run-header']

interface WikiSection {
  /** Anchor target. Also the scroll-spy key. */
  id: string
  /** Short label for the nav, where the full heading is a sentence. */
  nav: string
  /**
   * Which instruction file is authoritative for the section, not a restatement of
   * the title — see the note on `CardHeader`'s `eyebrow`. It is the question a
   * reader asks second: having read what a value means, where would they go to
   * change it.
   */
  eyebrow: string
  title: string
  intro: string
  body: ReactNode
}

/**
 * The nav label from a surface heading: everything before the em dash.
 *
 * `SURFACE_LABELS` stays the one home of the wording, so the nav cannot drift
 * from the heading it points at — a second, shorter set of four labels here is
 * exactly the copy that goes stale.
 */
function navLabel(label: string): string {
  return label.split(' — ')[0]
}

const SECTIONS: WikiSection[] = [
  ...SURFACE_ORDER.map((surface) => ({
    id: `wiki-${surface}`,
    nav: navLabel(SURFACE_LABELS[surface]),
    eyebrow: 'Field reference',
    title: SURFACE_LABELS[surface],
    intro: SURFACE_INTROS[surface],
    body: <ColumnList surface={surface} />,
  })),
  {
    id: 'wiki-rubric',
    nav: 'Scoring rubric',
    eyebrow: 'context/target-criteria',
    title: 'The scoring rubric',
    intro: `${wiki.rubric.length} axes, each scored 0–100 and then weighted. Adjustments apply to the axis score before weighting, and the result is clamped back into 0–100. The weights sum to ${wiki.weightSum} — the build fails if they do not, and so does a test on the rubric file itself. The published score is the weighted mean over the axes that were actually measured, and the share of weight those axes carry is the run’s coverage — so an axis nobody could measure lowers coverage instead of scoring zero.`,
    body: <RubricTable />,
  },
  {
    id: 'wiki-tiers',
    nav: 'Tier bands',
    eyebrow: 'context/target-criteria',
    title: 'Tier bands',
    intro:
      'What the score recommends, qualified by how much of the rubric was actually measured. A tier needs both numbers and the lower one decides — so a strong score on thin evidence lands low, and that is a statement about the evidence rather than about the target. A tier is advice to a person, so it is only usable with its reason attached.',
    body: <TierList />,
  },
  {
    id: 'wiki-disqualifiers',
    nav: 'Hard disqualifiers',
    eyebrow: 'context/target-criteria',
    title: 'Hard disqualifiers',
    intro:
      'Checked before any scoring. A hypothesis meeting any one of these is archived with the rule named — it does not get a score and no combination of strengths elsewhere rescues it. That is the difference between a bad target and a low score. The numbering has gaps: D2 (human homology) and D5 (resistance escape) were retired to recorded flags and their numbers are never re-used, so archived rows from earlier runs stay readable.',
    body: <DisqualifierList />,
  },
  {
    id: 'wiki-scope-flags',
    nav: 'Scope flags',
    eyebrow: 'context/target-criteria',
    title: 'Scope flags',
    intro:
      'Target types the program does not normally pursue are penalised, not filtered — an exceptional one should sit at the bottom of the leaderboard rather than be missing from it. The flag is what tells a reader why a good target ranks low.',
    body: <ScopeFlagList />,
  },
  {
    id: 'wiki-thresholds',
    nav: 'Thresholds',
    eyebrow: 'context/target-criteria',
    title: 'Thresholds',
    intro:
      'The program’s current working values, edited in one place. Treat a borderline call as unsettled until the program lead confirms it.',
    body: <ThresholdList />,
  },
  {
    id: 'wiki-vocabularies',
    nav: 'Graph vocabularies',
    eyebrow: 'context/kg-feature-catalog',
    title: 'Knowledge graph value vocabularies',
    intro: vocabularyIntro(),
    body: <VocabularyList />,
  },
]

/** Stable across renders, so the scroll-spy effect does not re-subscribe. */
const SECTION_IDS = SECTIONS.map((s) => s.id)

export function Wiki() {
  const active = useActiveSection(SECTION_IDS)

  return (
    // Full width, no prose measure. What is on this tab is mostly tables and
    // definition lists — the rubric's three columns, the thresholds' term/value
    // pairs, the vocabularies' value chips — and a 48rem cap wrapped every one of
    // them into a column down the left while the right half of the page sat empty.
    // The intros are a couple of sentences each, so nothing here needs the measure
    // a rationale paragraph does.
    <div className="space-y-6 pb-16">
      <Masthead />

      {/* Two columns from 1024px up, which is the narrowest this UI targets. The
          run rail takes 18rem out of the viewport before this element sees any of
          it, so the nav is kept to 11rem there and widens at 1280. Below 1024 it
          becomes a horizontal strip above the content rather than being dropped:
          this page is ten sections long and scrolling for one of them is the
          problem the nav exists to solve. */}
      <div className="animate-fade-in lg:grid lg:grid-cols-[11rem_minmax(0,1fr)] lg:gap-6 xl:grid-cols-[13rem_minmax(0,1fr)]">
        <Toc active={active} />
        <div className="min-w-0 space-y-6">
          {SECTIONS.map((section) => (
            <Panel key={section.id} section={section} />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * The head of the page, and the one full expansion of the product name in this UI.
 *
 * It carries the provenance note with it rather than sitting above a second card,
 * because the two answer one question — what this page is and how far it can be
 * trusted — and the answer a reader needs first is that it describes the
 * *committed* instruction tree. A scientist can publish an override to S3 without
 * a redeploy, so a run's own criteria version, shown in its header, is the
 * authority on what that run actually read. Stating that is cheaper and more
 * honest than trying to make a build-time page track a mutable mirror it cannot
 * see.
 */
function Masthead() {
  return (
    <Card className="animate-rise">
      <CardBody className="space-y-3 py-5">
        <p className="font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500">
          Data dictionary
        </p>
        <h1 className="font-display text-xl font-semibold tracking-tight text-slate-100">
          ASTRA — Advanced System for Target Research Agent
        </h1>
        <p className="max-w-3xl text-sm leading-relaxed text-slate-300">
          ASTRA proposes crop-protection target proteins, argues them against each
          other in a tournament, and publishes the ranking on the other two tabs.
          This page is its data dictionary: what every column means, what every
          value can be, and how a target got its score.
        </p>
        <p className="max-w-3xl text-xs leading-relaxed text-slate-400">
          Every value below is generated at build time from the same instruction
          files the agents read — the scoring rubric, the tier bands, the
          disqualifiers, and the knowledge graph’s value vocabularies. Nothing on
          this page is a second copy maintained by hand, so a change to a weight
          arrives here in the same commit that made it.
        </p>
        <p className="max-w-3xl text-xs leading-relaxed text-slate-400">
          It describes the <strong className="text-slate-300">committed</strong>{' '}
          instruction tree. Those files can also be overridden without a redeploy, so
          a run whose header reads <Code>context_source: s3</Code> may have scored
          against something slightly different; its{' '}
          <Code>context_version</Code> is what identifies the tree it read.
        </p>
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer select-none rounded hover:text-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            Built from {wiki.sources.length} files (build{' '}
            <span className="font-mono">{wiki.buildHash}</span>)
          </summary>
          <ul className="mt-2 space-y-0.5 font-mono">
            {wiki.sources.map((source) => (
              <li key={source.path}>
                {source.path}{' '}
                <span className="text-slate-600">
                  {source.bytes.toLocaleString()} B
                </span>
              </li>
            ))}
          </ul>
        </details>
      </CardBody>
    </Card>
  )
}

/**
 * The in-page nav.
 *
 * Real anchors, not scroll handlers: they are focusable, they work with a middle
 * click and they survive a page this long being read with the keyboard. The active
 * entry is `aria-current`, so the highlight is not colour alone.
 */
function Toc({ active }: { active: string }) {
  return (
    <nav
      aria-label="On this page"
      className="mb-4 lg:sticky lg:top-0 lg:mb-0 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto lg:pt-1"
    >
      <p className="font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500">
        On this page
      </p>
      <ul className="mt-2 flex gap-1 overflow-x-auto pb-1 lg:mt-3 lg:flex-col lg:gap-0.5 lg:overflow-x-visible lg:pb-0">
        {SECTIONS.map((section) => {
          const current = section.id === active
          return (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                aria-current={current ? 'true' : undefined}
                className={cn(
                  'block whitespace-nowrap rounded-md border-l-2 px-2.5 py-1.5 text-xs transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                  current
                    ? 'border-brand-500 bg-slate-800/70 font-medium text-slate-100'
                    : 'border-transparent text-slate-400 hover:bg-slate-800/50 hover:text-slate-200',
                )}
              >
                {section.nav}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

/**
 * Which section the reader is in.
 *
 * Deliberately the least clever version that works. The observer watches a band
 * across the top of the viewport rather than the whole of it — with the whole
 * viewport four sections intersect at once and the "active" one is whichever the
 * callback happened to list first — and when nothing intersects the last answer
 * stands, because the reader is then inside a section taller than the band and a
 * nav that blanks mid-section is worse than one that lags. `IntersectionObserver`
 * against the viewport is correct even though the scroll container is `main`: the
 * browser accounts for clipping by intervening scroll ancestors.
 *
 * It starts on the **first** section rather than on `null`, which is not cosmetic:
 * the masthead is taller than the band, so at scroll 0 nothing intersects and the
 * initial state is also the "keep the last answer" state — the nav rendered with
 * nothing marked until the reader scrolled, which is the one moment they are most
 * likely to be reading it. The top of the page *is* the first section, and a deep
 * link to a `#wiki-…` anchor scrolls before this effect runs, so the observer
 * corrects it on its first callback.
 */
function useActiveSection(ids: string[]): string {
  const [active, setActive] = useState<string>(ids[0])

  useEffect(() => {
    const nodes = ids
      .map((id) => document.getElementById(id))
      .filter((node): node is HTMLElement => node !== null)
    if (nodes.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting)
        if (visible.length === 0) return
        visible.sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
        )
        setActive(visible[0].target.id)
      },
      { rootMargin: '0px 0px -75% 0px' },
    )
    for (const node of nodes) observer.observe(node)
    return () => observer.disconnect()
  }, [ids])

  return active
}

/**
 * One section, on the shared card recipe.
 *
 * This was a fourth hand-rolled copy of `rounded-lg border border-slate-800
 * bg-slate-900/40` — the recipe `Card` exists to hold — and it had already
 * drifted from the other three in radius and in whether the background carried an
 * alpha.
 */
function Panel({ section }: { section: WikiSection }) {
  return (
    <Card id={section.id} className="scroll-mt-4">
      <CardHeader
        eyebrow={section.eyebrow}
        title={section.title}
        description={section.intro}
      />
      <CardBody>{section.body}</CardBody>
    </Card>
  )
}

function ColumnList({ surface }: { surface: Surface }) {
  return (
    <div className="space-y-2">
      {COLUMN_DOCS.filter((doc) => doc.surface === surface).map((doc) => (
        <Column key={`${surface}:${doc.label}`} doc={doc} />
      ))}
    </div>
  )
}

function Column({ doc }: { doc: ColumnDoc }) {
  const [open, setOpen] = useState(false)
  const detailId = useId()
  // A single-sourced caveat wins over any local wording. Three of them, kept
  // distinct rather than merged into one "corroboration note": the run count is
  // about runs that happened to agree, a champions pool was *selected* on those
  // same runs' verdicts, and a recap is about over-reading a reversal. Rendering
  // the wrong one of the three would be the paraphrase this mechanism prevents.
  const caution =
    doc.cautionFrom === 'runsIdentifiedNote'
      ? wiki.runsIdentifiedNote
      : doc.cautionFrom === 'championsNote'
        ? wiki.championsNote
        : doc.cautionFrom === 'recapNote'
          ? wiki.recapNote
          : doc.caution
  const values =
    doc.values ??
    (doc.valuesFrom === 'tiers'
      ? wiki.tiers.map((t) => ({
          // Both numbers, because a tier needs both and the lower one decides —
          // showing the score band alone reads as "this scored badly" for a target
          // that in fact scored well on too little evidence.
          value: t.tier,
          means: `score ${t.range}, coverage ${t.coverage} — ${t.means}`,
        }))
      : doc.valuesFrom === 'scopeFlags'
        ? wiki.scopeFlags.map((f) => ({ value: f.flag, means: f.text }))
        : doc.valuesFrom === 'stages'
          ? Object.entries(STAGE_LABELS).map(([value, means]) => ({ value, means }))
          : undefined)

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={detailId}
        className={cn(
          'flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-slate-800/40',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        )}
      >
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform',
            open && 'rotate-180',
          )}
        />
        <span className="min-w-0">
          <span className="font-display text-sm font-medium text-slate-200">
            {doc.label}
          </span>
          {doc.field && doc.field !== doc.label && (
            <span className="ml-2 font-mono text-2xs text-slate-500">
              {doc.field}
            </span>
          )}
          <span className="block text-xs leading-relaxed text-slate-400">
            <Md text={doc.meaning} />
          </span>
        </span>
      </button>

      {open && (
        <div
          id={detailId}
          className="space-y-2 border-t border-slate-800 px-3 py-2 pl-8"
        >
          <p className="text-xs leading-relaxed text-slate-400">
            <span className="font-display text-2xs uppercase tracking-eyebrow text-slate-500">
              How{' '}
            </span>
            <Md text={doc.computed} />
          </p>
          {values && (
            <dl className="space-y-1 text-xs">
              {values.map((v) => (
                <div key={v.value} className="sm:flex sm:gap-2">
                  <dt className="font-mono text-slate-300 sm:w-40 sm:shrink-0">
                    {v.value}
                  </dt>
                  <dd className="min-w-0 text-slate-400">
                    <Md text={v.means} />
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {caution && <Caution text={caution} />}
        </div>
      )}
    </div>
  )
}

function Caution({ text }: { text: string }) {
  return (
    <p className="flex gap-2 rounded-md border border-amber-900/50 bg-amber-950/20 p-2 text-xs leading-relaxed text-amber-200/90">
      <AlertTriangle aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        <Md text={text} />
      </span>
    </p>
  )
}

/**
 * The rubric, from the generated payload — never a hardcoded axis list. The bar
 * beside each weight is the axis's share of the whole rubric, which is also the
 * width of its segment in the axis spine on the ranking tables: read down the
 * column, these bars are that spine with every segment full.
 */
function RubricTable() {
  return (
    <table className="w-full text-left text-xs">
      <thead className="text-slate-500">
        <tr>
          <th scope="col" className="py-1 pr-3 font-medium">
            Axis
          </th>
          <th scope="col" className="py-1 pr-3 text-right font-medium">
            Weight
          </th>
          <th scope="col" className="py-1 font-medium">
            Scored on
          </th>
        </tr>
      </thead>
      <tbody className="align-top">
        {wiki.rubric.map((axis) => (
          <tr key={axis.axis} className="border-t border-slate-800/70">
            <td className="py-2 pr-3 font-display font-medium text-slate-200">
              {axis.axis}
            </td>
            <td className="py-2 pr-3 text-right">
              <span className="font-mono tabular-nums text-slate-300">
                {axis.weight}%
              </span>
              {/* Decorative: the number is right beside it, so a second
                  announcement of the same value would be noise. */}
              <span
                aria-hidden="true"
                className="mt-1 block h-1 w-16 overflow-hidden rounded-full bg-slate-800"
              >
                <span
                  className="block h-full rounded-full bg-brand-500"
                  style={{ width: `${axis.weight}%` }}
                />
              </span>
            </td>
            <td className="py-2 text-slate-400">
              <Md text={axis.basis} />
              <span className="mt-1 block text-slate-500">
                <Md text={axis.adjustments} />
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function TierList() {
  return (
    <dl className="space-y-1 text-xs">
      {wiki.tiers.map((tier) => (
        <div key={tier.tier} className="sm:flex sm:gap-2">
          <dt className="sm:w-48 sm:shrink-0">
            <span className="font-mono text-slate-200">{tier.tier}</span>
            <span className="ml-2 font-mono tabular-nums text-slate-500">
              {tier.range}
            </span>
            <span className="ml-1 font-mono tabular-nums text-slate-600">
              cov {tier.coverage}
            </span>
          </dt>
          <dd className="min-w-0 text-slate-400">
            <Md text={tier.means} />
          </dd>
        </div>
      ))}
    </dl>
  )
}

function DisqualifierList() {
  return (
    <dl className="space-y-2 text-xs">
      {wiki.disqualifiers.map((d) => (
        <div key={d.id}>
          <dt className="text-slate-200">
            <span className="font-mono text-slate-400">{d.id}</span>{' '}
            <Md text={d.rule} />
          </dt>
          <dd className="mt-0.5 text-slate-500">
            <Md text={d.why} />
          </dd>
        </div>
      ))}
    </dl>
  )
}

function ScopeFlagList() {
  return (
    <dl className="space-y-2 text-xs">
      {wiki.scopeFlags.map((flag) => (
        <div key={flag.flag}>
          <dt className="font-mono text-slate-200">{flag.flag}</dt>
          <dd className="mt-0.5 text-slate-400">
            <Md text={flag.text} />
          </dd>
        </div>
      ))}
    </dl>
  )
}

function ThresholdList() {
  return (
    <dl className="space-y-1 text-xs">
      {wiki.thresholds.map((t) => (
        <div key={t.threshold} className="sm:flex sm:gap-2">
          <dt className="text-slate-300 sm:w-64 sm:shrink-0">
            <Md text={t.threshold} />
          </dt>
          <dd className="min-w-0 text-slate-400">
            <Md text={t.value} />
          </dd>
        </div>
      ))}
    </dl>
  )
}

/** Grouped once, so the intro and the body agree on the domain count. */
function vocabulariesByDomain(): Map<string, typeof wiki.vocabularies> {
  const byDomain = new Map<string, typeof wiki.vocabularies>()
  for (const entry of wiki.vocabularies) {
    const list = byDomain.get(entry.domain) ?? []
    list.push(entry)
    byDomain.set(entry.domain, list)
  }
  return byDomain
}

function vocabularyIntro(): string {
  return `${wiki.vocabularies.length} fields whose values come from a fixed set, across ${vocabulariesByDomain().size} evidence domains. An absent field is not a bad value: the agents are required to report a domain they checked and found empty, because that is a measurement and "nobody looked" is not.`
}

/**
 * The knowledge graph's closed value sets, grouped by the domain file they came
 * from. This is the half of the page that answers "the rationale says
 * `soft_core` — what are the other options, and who decided?", which is
 * unanswerable from the board alone. Each row keeps its upstream database,
 * because which database said a thing is half of what makes the claim checkable.
 */
function VocabularyList() {
  return (
    <div className="space-y-4">
      {[...vocabulariesByDomain().entries()].map(([domain, entries]) => (
        <div key={domain}>
          <h3 className="font-display text-xs font-medium text-slate-300">
            {domain}
          </h3>
          <dl className="mt-1 space-y-1.5 text-xs">
            {entries.map((entry) => (
              <div key={`${entry.file}:${entry.field}`}>
                <dt className="text-slate-300">
                  <span className="font-mono">{entry.field}</span>
                  <span className="ml-2 text-slate-500">{entry.source}</span>
                </dt>
                <dd className="mt-0.5 flex flex-wrap gap-1">
                  {entry.values.map((value) => (
                    <span
                      key={value}
                      className="rounded-md border border-slate-700 bg-slate-800/60 px-1.5 py-0.5 font-mono text-2xs text-slate-300"
                    >
                      {value}
                    </span>
                  ))}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  )
}

/**
 * The two markdown constructs these source files actually use in a table cell:
 * `inline code` and **bold**. A markdown library would be a dependency for this,
 * and stripping the markers instead would turn `soft_core` into prose — the
 * backticks are how a reader tells a field value from a word.
 */
function Md({ text }: { text: string }) {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).filter(Boolean)
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('`') && part.endsWith('`')) {
          return <Code key={i}>{part.slice(1, -1)}</Code>
        }
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={i} className="font-medium text-slate-300">
              {part.slice(2, -2)}
            </strong>
          )
        }
        return <span key={i}>{part}</span>
      })}
    </>
  )
}

function Code({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-slate-800/80 px-1 py-0.5 font-mono text-[0.95em] text-slate-300">
      {children}
    </code>
  )
}
